import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import {
  OmsExportRequest,
  OmsProduct,
  Stage,
} from "../lusca-search-api/generated-src";
import { OmsReportsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

export default function useOmsExport(
  product: OmsProduct,
  snapshotId: string,
  mode: OmsExportRequest["mode"]
) {
  const storageKey = `oms-export:${product}:${snapshotId}:${mode}`;
  const [jobId, setJobId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(storageKey);
    } catch {
      return null;
    }
  });
  const [sent, setSent] = useState(false);
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const request = useRef<{ identity: string; id: string } | null>(null);
  const create = useMutation({
    mutationFn: async (
      criteria: Pick<OmsExportRequest, "filters" | "operation">
    ) => {
      const body = {
        mode,
        filters: criteria.filters || [],
        operation: criteria.operation || "and",
      };
      const identity = JSON.stringify([product, snapshotId, body]);
      // A lost response must be safe to retry without changing an accepted job.
      if (request.current?.identity !== identity) {
        request.current = { identity, id: uuidv4() };
      }
      return (
        await OmsReportsApiFactory().createOmsExport(
          product,
          Stage.Prod,
          snapshotId,
          { requestId: request.current.id, ...body }
        )
      ).data;
    },
    onSuccess: (job) => {
      try {
        // The dialog may have unmounted while the server accepted the request.
        sessionStorage.setItem(storageKey, job.jobId);
      } catch {
        // Export requests also work when browser storage is disabled.
      }
      setJobId(job.jobId);
      setSent(false);
    },
  });
  useEffect(() => {
    try {
      if (jobId) sessionStorage.setItem(storageKey, jobId);
      else sessionStorage.removeItem(storageKey);
    } catch {
      // Export requests also work when browser storage is disabled.
    }
  }, [jobId, storageKey]);
  const job = useQuery({
    queryKey: ["oms-export", product, snapshotId, mode, jobId],
    enabled: Boolean(jobId),
    retry: false,
    queryFn: async () =>
      (
        await OmsReportsApiFactory().getOmsExport(
          product,
          Stage.Prod,
          jobId as string
        )
      ).data,
    refetchInterval: (data, query) =>
      !query.state.error && (!data || data.status === "PREPARING")
        ? 3000
        : false,
    refetchIntervalInBackground: false,
  });
  const download = useMutation({
    mutationFn: async () =>
      (
        await OmsReportsApiFactory().getOmsExportDownload(
          product,
          Stage.Prod,
          jobId as string
        )
      ).data,
    onSuccess: (result) => {
      window.location.assign(result.url);
      setSent(true);
    },
  });
  const expiresAt = job.data?.expiresAt;
  useEffect(() => {
    const deadline = expiresAt ? new Date(expiresAt).getTime() : NaN;
    function checkDeadline() {
      setDeadlinePassed(Boolean(jobId) && Date.now() >= deadline);
    }
    checkDeadline();
    const timer = Number.isFinite(deadline)
      ? window.setTimeout(checkDeadline, Math.max(0, deadline - Date.now()))
      : undefined;
    window.addEventListener("focus", checkDeadline);
    document.addEventListener("visibilitychange", checkDeadline);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", checkDeadline);
      document.removeEventListener("visibilitychange", checkDeadline);
    };
  }, [jobId, expiresAt]);
  function responseStatus(error: unknown): number | undefined {
    return (error as { response?: { status?: number } } | null)?.response
      ?.status;
  }
  const expired =
    deadlinePassed ||
    responseStatus(job.error) === 410 ||
    responseStatus(download.error) === 410;
  const unavailable =
    responseStatus(job.error) === 404 || responseStatus(download.error) === 404;
  const createConflict = responseStatus(create.error) === 409;
  function reset() {
    request.current = null;
    setJobId(null);
    setSent(false);
    setDeadlinePassed(false);
    create.reset();
    download.reset();
  }
  return {
    create,
    job,
    download,
    jobId,
    sent,
    expired,
    unavailable,
    createConflict,
    reset,
  };
}
